<script setup lang="ts">
/**
 * @author Giorgi Tseradze
 */

//
import { onMounted, ref } from "vue";
import LeftArrowSVG from "../SVG/LeftArrowSVG.vue";
import RightArrowSVG from "../SVG/RightArrowSVG.vue";

const props = defineProps<{
  date?: string;
  nextActive: boolean;
  prevActive: boolean;
}>();

const emit = defineEmits(["prev", "next"]);
const day = ref();
const year = ref();
const month = ref();

const handleDate = () => {
  if (!props?.date) return;
  const dateParts = props?.date?.split("-");
  year.value = dateParts[0];
  const dayNum = dateParts[2].split(" ")[0];
  const monthNum = dateParts[1];

  const monthNames: any = {
    "01": "calendar-january",
    "02": "calendar-february",
    "03": "calendar-march",
    "04": "calendar-april",
    "05": "calendar-may",
    "06": "calendar-june",
    "07": "calendar-july",
    "08": "calendar-august",
    "09": "calendar-september",
    "10": "calendar-october",
    "11": "calendar-november",
    "12": "calendar-december",
  };

  const dayNames: any = {
    "01": "1",
    "02": "2",
    "03": "3",
    "04": "4",
    "05": "5",
    "06": "6",
    "07": "7",
    "08": "8",
    "09": "9",
  };
  if (Number(dayNum) < 10) {
    day.value = dayNames[dayNum];
  } else {
    day.value = dayNum;
  }
  month.value = monthNames[monthNum];
};
const handlePrev = () => {
  emit("prev");
  setTimeout(() => {
    handleDate();
  }, 1);
};

const handleNext = () => {
  emit("next");
  setTimeout(() => {
    handleDate();
  }, 1);
};
onMounted(() => {
  handleDate();
});
</script>

<template>
  <div class="date-card d-flex dir-col al-center j-center">
    <div class="d-flex">
      <div class="date-card__date-day">
        <h2 class="hl-1-plus">{{ day }}</h2>
      </div>
      <div class="d-flex dir-col j-center">
        <div class="date-card__date-year">
          <p class="p-14">{{ year }}</p>
        </div>
        <div class="date-card__date-month">
          <h2 class="hl-2-light">{{ $t(month?.toLowerCase() || "") }}</h2>
        </div>
      </div>
    </div>
    <div class="date-card__date-border"></div>
    <div class="date-card__arrows">
      <div
        v-if="prevActive"
        @click="handlePrev"
        class="date-card__arrows-left d-flex al-center j-center"
      >
        <LeftArrowSVG class="date-card__arrows-left-svg" />
        <p class="p-14 uppercase">{{ $t("previous") }}</p>
      </div>
      <div
        v-if="nextActive"
        @click="handleNext"
        class="date-card__arrows-right d-flex al-center"
      >
        <p class="p-14 uppercase">{{ $t("next") }}</p>
        <RightArrowSVG class="date-card__arrows-right-svg" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.date-card {
  background-color: $white;
  border: 1px solid $brightGray;
  margin-right: 48px;
  @include maxq(desktop-lg) {
    margin-right: 24px;
  }
  @include maxq(mobile) {
    width: 220px;
    height: 264px;
  }
  &__date {
    border: 1px solid $brightGray;
    &-day {
      h2 {
        @include weight(300);
        font-size: 107px;
        line-height: 84px;
        @include maxq(desktop-lg) {
          font-size: 80px;
          line-height: 60px;
        }
        @include maxq(desktop-sm) {
          font-size: 64px;
        }
      }
    }
    &-year {
      padding-left: 16px;
      padding-bottom: 1px;

      p {
        @include weight(300);
        line-height: 22px;
      }
    }
    &-month {
      margin-top: 14px;
      padding-left: 16px;
      @include maxq(desktop-2k) {
        margin-top: 10px;
      }
      @include maxq(desktop-lg) {
        margin-top: initial;
      }
      h2 {
        line-height: 32px;

        @include maxq(desktop-sm) {
          font-size: 24px;
        }
      }
    }
    &-border {
      margin: 40px 0px 23px 0px;
      border: 1px solid $brightGray;
      width: 45.454545454%;
      @include maxq(desktop-sm) {
        margin: 24px 0px 23px 0px;
      }
    }
  }

  &__arrows {
    &-left {
      cursor: pointer;
      @include hover {
        .date-card__arrows-left-svg {
          transform: translateX(-15%);
          @include easeOut(500, transform);
        }
      }
      &-svg {
        transform: translateX(0);
        @include easeOut(500, transform);
      }
      p {
        @include weight(300);
        padding-left: 13px;

        @include maxq(desktop-2k) {
          font-size: 20px;
          line-height: 18px;
        }
        @include maxq(desktop-lg) {
          font-size: unset;
          line-height: unset;
        }
        @include maxq(desktop-sm) {
          padding-left: 8px;
        }
      }
    }
    &-right {
      padding-top: 23px;
      cursor: pointer;
      @include hover {
        .date-card__arrows-right-svg {
          transform: translateX(15%);
          @include easeOut(500, transform);
        }
      }
      &-svg {
        transform: translateX(0);
        @include easeOut(500, transform);
      }
      p {
        @include weight(300);
        padding-right: 13px;
        line-height: 18px;
        @include maxq(desktop-sm) {
          padding-left: 8px;
        }
      }
    }
  }
}
</style>
